/**
 * PataKare — Central Field Validation Utilities
 *
 * Single source of truth for form field validation across the whole app.
 * Har validator ya to `null` (valid) ya ek error string (invalid) return karta hai,
 * taaki manual useState forms me seedha use ho sake:
 *
 *   const err = validatePhone(value);
 *   if (err) setError(err);
 *
 * Pure boolean checks ke liye `isValidX` helpers bhi hain (existing code se compatible).
 *
 * ⚠️ Ye sirf frontend UX ke liye hai — backend (PHP) pe bhi mirror validation zaroori hai.
 */

// ============= Regex (single source) =============
/** India mobile: 10 digits, 6-9 se start */
export const RE_PHONE_IN = /^[6-9]\d{9}$/;
/** India pincode: exactly 6 digits, pehla digit 1-9 */
export const RE_PINCODE_IN = /^[1-9]\d{5}$/;
/** Email (practical, RFC-lite) */
export const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Name: letters, space, dot, hyphen, apostrophe (no digits/special) */
export const RE_NAME = /^[A-Za-z][A-Za-z\s.'-]*$/;
/** Only digits */
export const RE_DIGITS = /^\d+$/;

// ============= Boolean helpers (existing code compatible) =============
/** 10-digit India mobile check (non-digit chars strip karke) */
export const isValidPhone = (phone: string): boolean =>
  RE_PHONE_IN.test(String(phone ?? '').replace(/\D/g, ''));

/** Alias — kuch files `isValidMobile` naam use karti hain */
export const isValidMobile = isValidPhone;

export const isValidEmail = (email: string): boolean =>
  RE_EMAIL.test(String(email ?? '').trim());

export const isValidPincode = (pin: string): boolean =>
  RE_PINCODE_IN.test(String(pin ?? '').replace(/\D/g, ''));

// ============= Field validators (error-string | null) =============

/** Mandatory field — khaali na ho */
export function validateRequired(value: unknown, label = 'This field'): string | null {
  const v = typeof value === 'string' ? value.trim() : value;
  if (v === '' || v === null || v === undefined) return `${label} is required`;
  return null;
}

/** Name — sirf letters/space/dot/hyphen/apostrophe */
export function validateName(value: string, label = 'Name'): string | null {
  const v = String(value ?? '').trim();
  if (!v) return `${label} is required`;
  if (v.length < 2) return `${label} must be at least 2 characters`;
  if (!RE_NAME.test(v)) return `${label} can only contain letters`;
  return null;
}

/** Email */
export function validateEmail(value: string, label = 'Email'): string | null {
  const v = String(value ?? '').trim();
  if (!v) return `${label} is required`;
  if (!RE_EMAIL.test(v)) return `Enter a valid ${label.toLowerCase()}`;
  return null;
}

/** India mobile (10 digits) */
export function validatePhone(value: string, label = 'Mobile number'): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return `${label} is required`;
  if (!RE_PHONE_IN.test(digits)) return `Enter a valid 10-digit ${label.toLowerCase()}`;
  return null;
}

/** India pincode (6 digits) */
export function validatePincode(value: string, label = 'Pincode'): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return `${label} is required`;
  if (!RE_PINCODE_IN.test(digits)) return `Enter a valid 6-digit ${label.toLowerCase()}`;
  return null;
}

/** Only-digits field (license no. etc.) */
export function validateDigits(value: string, label = 'This field'): string | null {
  const v = String(value ?? '').trim();
  if (!v) return `${label} is required`;
  if (!RE_DIGITS.test(v)) return `${label} can only contain digits`;
  return null;
}

/** Max length guard (DB column overflow rokne ke liye) */
export function validateMaxLength(value: string, max: number, label = 'This field'): string | null {
  if (String(value ?? '').length > max) return `${label} must be at most ${max} characters`;
  return null;
}

/** Password strength (register/reset forms) */
export function validatePassword(value: string, min = 8): string | null {
  const v = String(value ?? '');
  if (!v) return 'Password is required';
  if (v.length < min) return `Password must be at least ${min} characters`;
  if (!/[A-Za-z]/.test(v) || !/\d/.test(v)) return 'Password must contain letters and numbers';
  return null;
}

/** Confirm-password match */
export function validateConfirmPassword(pwd: string, confirm: string): string | null {
  if (!confirm) return 'Please confirm your password';
  if (pwd !== confirm) return 'Passwords do not match';
  return null;
}

/**
 * Date not in the future (e.g. DOB).
 * `value` = yyyy-mm-dd string ya Date.
 */
export function validateNotFuture(value: string | Date, label = 'Date'): string | null {
  if (!value) return `${label} is required`;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return `Enter a valid ${label.toLowerCase()}`;
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  if (d > today) return `${label} cannot be in the future`;
  return null;
}

/** Date not in the past (e.g. expiry, camp date). */
export function validateNotPast(value: string | Date, label = 'Date'): string | null {
  if (!value) return `${label} is required`;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return `Enter a valid ${label.toLowerCase()}`;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d < today) return `${label} cannot be in the past`;
  return null;
}

// ============= File upload (client-side) =============
export interface FileValidationOptions {
  /** Max size in MB */
  maxSizeMB?: number;
  /** Allowed MIME types, e.g. ['image/jpeg','image/png','application/pdf'] */
  allowedTypes?: string[];
  label?: string;
}

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const DOC_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

/** File size + MIME check (frontend). Backend pe finfo se dobara check zaroori. */
export function validateFile(file: File | null | undefined, opts: FileValidationOptions = {}): string | null {
  const { maxSizeMB = 5, allowedTypes = IMAGE_TYPES, label = 'File' } = opts;
  if (!file) return `${label} is required`;
  if (allowedTypes.length && !allowedTypes.includes(file.type)) {
    const nice = allowedTypes.map((t) => t.split('/')[1]?.toUpperCase()).join(', ');
    return `${label} must be one of: ${nice}`;
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    return `${label} must be smaller than ${maxSizeMB} MB`;
  }
  return null;
}

// ============= Multi-field helper =============
/**
 * Ek object ke against validators chalao, saare errors return karo.
 *
 *   const errors = validateFields({
 *     name:  () => validateName(form.name),
 *     phone: () => validatePhone(form.phone),
 *   });
 *   if (Object.keys(errors).length) { setErrors(errors); return; }
 */
export function validateFields(
  rules: Record<string, () => string | null>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key in rules) {
    const err = rules[key]();
    if (err) errors[key] = err;
  }
  return errors;
}

/** Sanitize digits-only input (onChange me use karo number fields pe) */
export const digitsOnly = (value: string, maxLen?: number): string => {
  const d = String(value ?? '').replace(/\D/g, '');
  return maxLen ? d.slice(0, maxLen) : d;
};

/** Sanitize name input (letters/space/dot/hyphen/apostrophe allow) */
export const nameCharsOnly = (value: string): string =>
  String(value ?? '').replace(/[^A-Za-z\s.'-]/g, '');
